import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';

const { FiUser, FiPlus, FiArrowRight, FiSearch, FiMapPin, FiCreditCard } = FiIcons;

const UserList = () => {
  const { customUsers, addCustomUser, analyticsData } = useAnalytics();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    userID: '',
    firstName: '',
    lastName: '',
    postalCode: '',
    state: '',
    membershipType: 'Free',
    membershipStatus: 'Active'
  });
  const [searchTerm, setSearchTerm] = useState('');

  const handleAddUser = async (e) => {
    e.preventDefault();
    await addCustomUser(newUser);
    setNewUser({
      userID: '',
      firstName: '',
      lastName: '',
      postalCode: '',
      state: '',
      membershipType: 'Free',
      membershipStatus: 'Active'
    });
    setShowAddModal(false);
  };

  const getUserLastSeen = (userId) => {
    const userViews = analyticsData.pageViews.filter(pv => pv.userSerial === userId);
    if (userViews.length === 0) return 'Never';
    const lastView = Math.max(...userViews.map(v => v.timestamp));
    return new Date(lastView).toLocaleDateString();
  };

  const filteredUsers = customUsers.filter(u => 
    (u.user_serial || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (u.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
    (u.last_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900">User Identification</h2>
          <p className="text-sm text-slate-500">Managing {customUsers.length} identified profiles</p>
        </div>
        <button 
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
        >
          <SafeIcon icon={FiPlus} /> Add User
        </button>
      </div>

      <div className="p-4 border-b border-slate-100 bg-slate-50 flex gap-4">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search by ID or name..." 
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider font-bold">
            <tr>
              <th className="px-6 py-4">User Details</th>
              <th className="px-6 py-4">Location</th>
              <th className="px-6 py-4">Membership</th>
              <th className="px-6 py-4">Last Seen</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {filteredUsers.map((user) => (
              <tr key={user.id} className="hover:bg-blue-50/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                      {user.first_name ? user.first_name[0] : '?'}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">{user.first_name} {user.last_name}</div>
                      <div className="text-xs font-mono font-medium text-blue-600">{user.user_serial}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1.5 text-slate-600 text-xs font-medium">
                    <SafeIcon icon={FiMapPin} className="text-slate-400" />
                    {user.state || 'N/A'}, {user.postal_code || 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-900 text-xs">{user.membership_type}</span>
                    <span className={`text-[10px] font-bold uppercase mt-0.5 ${user.membership_status === 'Active' ? 'text-emerald-600' : 'text-rose-500'}`}>
                      {user.membership_status}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-500 text-xs font-medium">
                  {getUserLastSeen(user.user_serial)}
                </td>
                <td className="px-6 py-4 text-right">
                  <Link 
                    to={`/user/${user.user_serial}`} 
                    className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-lg transition-all"
                  >
                    <SafeIcon icon={FiArrowRight} />
                  </Link>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500 text-sm">
                  No users found matching your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden border border-slate-200"
          >
            <div className="p-6 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Create User Profile</h3>
                <p className="text-xs text-slate-500 mt-1">Link identified IDs to real user metadata</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-rose-500 transition-colors">
                <SafeIcon icon={FiIcons.FiX} className="text-xl" />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Website ID / Serial</label>
                  <input 
                    required 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all" 
                    placeholder="e.g., v_8n2m9s1l" 
                    value={newUser.userID} 
                    onChange={e => setNewUser({...newUser, userID: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">First Name</label>
                  <input 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all" 
                    value={newUser.firstName} 
                    onChange={e => setNewUser({...newUser, firstName: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Last Name</label>
                  <input 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all" 
                    value={newUser.lastName} 
                    onChange={e => setNewUser({...newUser, lastName: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">State</label>
                  <input 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all" 
                    placeholder="NY" 
                    value={newUser.state} 
                    onChange={e => setNewUser({...newUser, state: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Postal Code</label>
                  <input 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all" 
                    placeholder="10001" 
                    value={newUser.postalCode} 
                    onChange={e => setNewUser({...newUser, postalCode: e.target.value})} 
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Membership Type</label>
                  <select 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all bg-white" 
                    value={newUser.membershipType} 
                    onChange={e => setNewUser({...newUser, membershipType: e.target.value})}
                  >
                    <option>Free</option>
                    <option>Silver</option>
                    <option>Gold</option>
                    <option>Platinum</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Status</label>
                  <select 
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all bg-white" 
                    value={newUser.membershipStatus} 
                    onChange={e => setNewUser({...newUser, membershipStatus: e.target.value})}
                  >
                    <option>Active</option>
                    <option>Expired</option>
                    <option>Pending</option>
                    <option>Cancelled</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-6 border-t border-slate-100">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest hover:text-slate-900 transition-colors">Cancel</button>
                <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-blue-700 shadow-sm transition-all">Save Profile</button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default UserList;